import { motion } from "framer-motion";
import { PlayCircle, Sparkles } from "lucide-react";
import { GlowOrbs } from "./Primitives";
import { WHATSAPP_NUMBER, WHATSAPP_MSG } from "@/lib/siteData";

const HERO_BG = "https://images.unsplash.com/photo-1654198340681-a2e0fc449f1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG5lb24lMjBibHVlJTIwcHVycGxlJTIwd2F2ZXMlMjBkYXJrfGVufDB8fHx8MTc4NjIwNDgzNnww&ixlib=rb-4.1.0&q=85";
const PHONE_IMG = "https://static.prod-images.emergentagent.com/jobs/85a17312-4b6e-4641-a2c3-1dc55f95e7c2/images/c77ae1946aac6a6f5059c480ad8cce0417ca6b6c6de6cc86736ff48b54acd489.jpeg";

const lines = ["O FUTURO DO", "ENTRETENIMENTO", "NA SUA TELA"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.8, delay: 0.2 + i * 0.12, ease: [0.33, 1, 0.68, 1] } }),
};

export const Hero = () => {
  const wa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="topo" className="relative min-h-screen overflow-hidden pt-28 md:pt-0 md:flex md:items-center">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/40 via-[#05050A]/70 to-[#05050A]" />
      </div>
      <GlowOrbs />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-4 py-16 md:grid-cols-12 md:px-8">
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 backdrop-blur-md"
          >
            <Sparkles className="h-4 w-4 text-[#06B6D4]" />
            <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-cyan-300">
              +25.000 clientes satisfeitos
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter md:text-6xl lg:text-7xl">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                >
                  {i === 1 ? (
                    <span className="bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] bg-clip-text text-transparent">
                      {line}
                    </span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-6 max-w-xl font-body text-base text-slate-300 md:text-lg"
          >
            Mais de 100 mil filmes, séries e canais ao vivo em qualidade 4K, sem travamentos e
            com suporte 24 horas. Assista onde e quando quiser.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-9 flex flex-col gap-4 sm:flex-row"
          >
            <button
              data-testid="hero-plans-cta"
              onClick={() => scrollTo("#planos")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#7c3aed] px-8 py-4 font-display font-bold text-white glow-purple transition-transform hover:scale-[1.04]"
            >
              Ver Planos
            </button>
            <a
              data-testid="hero-trial-cta"
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/15 px-8 py-4 font-display font-bold text-white transition-colors hover:border-[#06B6D4] hover:text-[#06B6D4]"
            >
              <PlayCircle className="h-5 w-5" /> Testar Grátis
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-10 flex gap-8"
          >
            {[["99,9%", "Estabilidade"], ["4K", "Qualidade"], ["24/7", "Suporte"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-display text-2xl font-black md:text-3xl">{v}</div>
                <div className="font-mono-accent text-xs uppercase tracking-widest text-slate-400">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative md:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: -6 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ rotate: 0, scale: 1.02 }}
            className="mx-auto aspect-square max-w-[420px] overflow-hidden rounded-3xl border border-white/10 shadow-[0_20px_80px_rgba(139,92,246,0.35)]"
          >
            <img src={PHONE_IMG} alt="NovaStream - sua TV completa" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
